<template>
  <div class="flex flex-col gap-8">
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div
        v-for="(stat, index) in reportStatistics"
        :key="stat.label"
        class="rounded-lg p-6"
        :class="index === 0 ? 'bg-gradient-to-b from-secondary-subtle to-white' : 'bg-white'"
        style="box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);"
      >
        <div class="flex items-center gap-2 mb-4">
          <div
            class="w-7 h-7 rounded flex items-center justify-center shrink-0"
            :class="index === 0 ? 'bg-white border border-gray-light' : 'bg-secondary-subtle'"
          >
            <FontAwesomeIcon
              :icon="stat.icon"
              class="text-secondary text-xs"
            />
          </div>

          <span class="text-xs font-semibold text-gray-dark uppercase tracking-wide font-admin">{{ stat.label }}</span>
        </div>

        <p
          class="font-admin font-bold text-primary mb-0 leading-none"
          style="font-size: 2rem;"
        >
          <span
            v-if="stat.prefix"
            class="text-xl"
          >
            {{ stat.prefix }}
          </span>

          {{ stat.value }}
        </p>

        <p class="text-sm text-gray-dark font-admin mb-0 mt-2">
          {{ stat.subtitle }}
        </p>
      </div>
    </div>

    <div>
      <div class="mb-4">
        <h2 class="font-bold text-primary font-admin text-base mb-0.5">
          Breakdown by photo type
        </h2>

        <p class="text-sm text-gray-dark font-admin">
          What people bought, and how much it earned.
        </p>
      </div>

      <AppTable :if-looped-items-exist="true">
        <template #headings>
          <th>Type</th>
          <th>Style</th>
          <th>Delivery</th>
          <th>Size</th>

          <th class="text-right">
            Units
          </th>

          <th class="text-right">
            Revenue
          </th>

          <th class="text-right">
            Share
          </th>
        </template>

        <template
          v-if="event.report"
          #rows
        >
          <tr
            v-for="(row, index) in event.report.photoTypes"
            :key="index"
          >
            <td>{{ row.type }}</td>
            <td>{{ row.style }}</td>
            <td>{{ row.delivery }}</td>
            <td>{{ row.size }}</td>

            <td class="text-right">
              {{ row.count }}
            </td>

            <td class="text-right font-bold">
              {{ string.formatCurrency(row.revenue) }}
            </td>

            <td>
              <div class="flex items-center justify-end gap-3">
                <div class="w-28 h-1.5 bg-gray-light rounded-full overflow-hidden">
                  <div
                    class="h-full bg-secondary rounded-full"
                    :style="{ width: `${calculatePhotoTypeRevenueShare(row)}%` }"
                  />
                </div>

                <span class="w-8 text-right mr-2">
                  {{ calculatePhotoTypeRevenueShare(row) }}%
                </span>
              </div>
            </td>
          </tr>

          <tr>
            <td
              colspan="4"
              class="font-bold"
            >
              Total
            </td>

            <td style="text-align: right; font-weight: 700;">
              {{ event.report.totalOrderItems }}
            </td>

            <td style="text-align: right; font-weight: 700;">
              {{ string.formatCurrency(event.report.totalSalesAmount) }}
            </td>

            <td>
              <div class="flex items-center justify-end gap-3">
                <div class="w-28 h-1.5 bg-gray-light rounded-full overflow-hidden">
                  <div class="h-full bg-secondary rounded-full w-full" />
                </div>

                <span class="w-8 text-right font-bold mr-2">
                  100%
                </span>
              </div>
            </td>
          </tr>
        </template>
      </AppTable>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue';

import { AppTable } from '@/components/app';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faImages, faMoneyBillWave, faReceipt } from '@fortawesome/pro-solid-svg-icons';

import { useString } from '@/composables';

const string = useString();

const event = inject('event');

const reportStatistics = computed(() => {
  const { totalSalesAmount = 0, totalOrders = 0, totalOrderItems = 0 } = (event.value?.report || {});
  const averageOrderValue = totalOrders > 0 ? Math.round(totalSalesAmount / totalOrders) : 0;

  return [
    {
      label: 'Total Sales',
      icon: faMoneyBillWave,
      prefix: 'R',
      value: string.formatCurrency(totalSalesAmount, false, null),
      subtitle: 'incl. VAT',
    },
    {
      label: 'Items Purchased',
      icon: faImages,
      prefix: null,
      value: totalOrderItems,
      subtitle: `across ${totalOrders} ${totalOrders === 1 ? 'order' : 'orders'}`,
    },
    {
      label: 'Avg Order Value',
      icon: faReceipt,
      prefix: 'R',
      value: string.formatCurrency(averageOrderValue, false, null),
      subtitle: `${totalOrders} ${totalOrders === 1 ? 'order' : 'orders'}`,
    },
  ];
});

const calculatePhotoTypeRevenueShare = (photoType) => {
  if (event.value && event.value.report.totalSalesAmount > 0) {
    return Math.round((photoType.revenue / event.value.report.totalSalesAmount) * 100 * 100) / 100;
  }

  return 0;
};
</script>
